import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { AuthenticationService, IWebAppUser } from '@services/authentication.service'
import { Account, AccountRole, AccountRoleLabels, AccountsService, IAccount, IAccountUser } from '@services/accounts.service';
import { GlobalFunctions, ISQLResult, SnackType } from '@global/GlobalFunctions';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil, distinctUntilChanged, filter, finalize, tap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { UserInvitationComponent } from 'src/app/dialogs/user-invitation/user-invitation.component';
import { CommentDialogComponent } from 'src/app/dialogs/comment-dialog/comment-dialog.component';
import { IAccountInvite, IAccountInviteDetail, InvitesService } from '@services/invites.service';
import { convertEpochToDateString } from '@functions/functions'
import { AddAccountUserComponent } from 'src/app/dialogs/add-account-user/add-account-user.component';
import { MatTableDataSource } from '@angular/material/table';

/**
 * AccountManageOrganisationUsersComponent
 */
@Component({
	selector: 'app-account-manage-users',
	templateUrl: './account-manage-users.component.html',
	styleUrls: ['./account-manage-users.component.scss']
})
export class AccountManageOrganisationUsersComponent implements OnInit, OnChanges, OnDestroy {

	public choiceSelected: any = 0; //"Pending";

	@ViewChild('tabs') tabsObj: any;

	/** Account GUID */
	@Input() accountGUID: string;

	/** System Users List */
	public accountUsers: IAccountUser[];
	/** filteredAccountUsers */
	public filteredAccountUsers: MatTableDataSource<IAccountUser>;
	/** Invited Users List */
	public invitedUsers: IAccountInviteDetail[];
	/** Filtered Invites */
	public filteredinvites: IAccountInviteDetail[];
	/** My Accounts List */
	public myAccounts: IAccount[];
	/** Selected Account */
	public selectedAccount: IAccount;
	/** Current User */
	public thisUser: IWebAppUser;
	/** Observable Subject Finalise */
	private finalise = new Subject<void>();
	/** Selected Invite */
	private selectedInvite: IAccountInviteDetail;
	/** Show all Canceled Invitions */
	public showCanceled = false
	/** Show all Accepted Invitions */
	public showAccepted = false
	/** Show all Pending Invitions */
	public showPending = false
	/** showingWebsiteAdmins */
	public showingWebsiteAdmins: boolean = false;
	/** editingUserRole */
	public editingUserRole: IAccountUser | undefined;
	/** accountRoleObject */
	public accountRoleArray: { AccountRole: AccountRole, Description: string }[] = [];
	/** accountRoleFields */
	public accountRoleFields: Object = { text: "Description", value: "AccountRole" };
	/** deletedAccountUsers - issue #68 */
	public deletedAccountUsers: IAccountUser[] = [];

	/**
	 * Default Constructor
	 * @param authenticationService authenticationService
	 * @param accountsService accountsService
	 * @param inviteService inviteService
	 * @param globalFunctions globalFunctions
	 * @param siteSubscriptions siteSubscriptions
	 * @param organisationDataService organisationDataService
	 * @param dialog dialog
	 */
	constructor(
		private authenticationService: AuthenticationService,
		private accountsService: AccountsService,
		private inviteService: InvitesService,
		private globalFunctions: GlobalFunctions,
		private siteSubscriptions: siteSubscriptions,
		public dialog: MatDialog
	) {
		// Validate the user
		this.authenticationService.currentUser$.pipe(
			takeUntil(this.finalise),
			distinctUntilChanged()
		).subscribe({
			next: (currentUser) => {
				// Check we have a validated User
				if (currentUser && !this.thisUser) {
					this.thisUser = <IWebAppUser>currentUser
				}
			},
			error: (error) => { this.globalFunctions.HandleError(error, "Error: this.authenticationService.currentUser$", false) },
			//complete: () => { console.log("this.authenticationService.currentUser$: Complete"); }
		})
	}

	/**
	 * Subscribes to changes in the input values and updates the selected asset object
	 * @param changes
	 */
	ngOnChanges(changes: SimpleChanges) {
		if (this.accountGUID != '') {
			this.subscribeAccounts(this.accountGUID)

		}
	}

	/**
	 * Page On Initialise Event Handler
	 */
	ngOnInit(): void {
		this.accountRoleArray = Object.entries(AccountRoleLabels)
			.map((x) => ({ AccountRole: parseInt(x[0]), Description: x[1] }))
			.filter((x) => x.AccountRole != 999);

		// Get Users for the account
		this.subscribeAccountUsers();
	}

	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();
		}
		catch { }
	}

	/**
	 * subscribeAccountUsers
	 */
	subscribeAccountUsers() {

		this.siteSubscriptions.accountUsers$.pipe(
			tap(() => {
				this.accountUsers = [];
				this.filteredAccountUsers = new MatTableDataSource(this.accountUsers);
			}),
			takeUntil(this.finalise),
			filter(users => !!users),
			distinctUntilChanged(),
		).subscribe({
			next: (users) => {
				this.accountUsers = users.filter((x: IAccountUser) => !x.Deleted);
				/* deleted user list for issue #68 */
				this.deletedAccountUsers = users.filter((x: IAccountUser) => x.Deleted)
					.sort((a: IAccountUser, b: IAccountUser) => (b.DateDeleted ?? 0) - (a.DateDeleted ?? 0));
				this.filteredAccountUsers = new MatTableDataSource(this.accountUsers);
				this.showWebsiteAdmins(this.showingWebsiteAdmins);
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: this.siteSubscriptions.accountUsers$", false)

			},
			//complete: () => { console.log("this.siteSubscriptions.accountUsers$: Complete"); }
		})
	}

	/**
	 * Subscribe to the list of My accounts
	 */
	subscribeAccounts(accountGUID: String) {

		this.siteSubscriptions.myAccounts$.pipe(
			takeUntil(this.finalise),
			distinctUntilChanged()
		).subscribe({
			next: (myAccounts) => {
				if (myAccounts) {
					this.myAccounts = myAccounts;
					this.myAccounts = this.siteSubscriptions.normalizeFeatures(this.myAccounts) as Account[];
					this.selectedAccount = <IAccount>this.myAccounts.find(({ GUID }) => GUID.toUpperCase() === accountGUID)

					// Check we have an account
					if (this.selectedAccount) {

						//this.selectedAccount.Admin = true;
						this.accountGUID = this.selectedAccount.GUID;

						// Get Users for the account
						this.getAccountUsers(this.accountGUID);

						// Get Users for the account
						this.getAccountInvites(this.accountGUID);

					}
				}
			},
			error: (error) => { this.globalFunctions.HandleError(error, "Error: this.siteSubscriptions.myAccounts$", false) },
			//complete: () => { console.log("this.siteSubscriptions.myAccounts$: Complete"); }
		})
	}

	/**
	 * Get list of invited Users
	 * @param accountGUID
	 */
	getAccountInvites(accountGUID: string) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.invitedUsers = []
		this.filteredinvites = []

		let data = {
			"publicUID": this.thisUser.Sub,
			"accountGUID": accountGUID,
			"ShowAll": true
		}

		this.inviteService.getInvitations$(data).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((invites) => !!invites),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (invites) => {
				this.invitedUsers = <IAccountInviteDetail[]>invites
				this.filterInvites()
				this.globalFunctions.displaySpinner(false)
			},
			error: (error) => {
				if (error.status = 404) {
					// console.log("No Invitations");
					return
				}
				console.error("this.inviteService.getInvitations$:", error)
			},
			//complete: () => { console.log("this.inviteService.getInvitations$: Complete"); }
		}
		)
	}

	public showingCancelled: boolean = false;

	/**
	 * Filter the Invites List
	 * @param showAll
	 */
	filterInvites() {
		this.showingCancelled = false;
		if (!this.selectedAccount.Admin) {
			this.choiceSelected = 0;//"Pending"
		}

		this.setChoice(this.choiceSelected)

		// if (this.choiceSelected == 0) { //"Pending") {
		// 	this.filteredinvites = this.invitedUsers.filter(({ Canceled, Accepted }) => Canceled == false && Accepted == false)
		// }
		// if (this.choiceSelected == 1) { //"Accepted") {
		// 	this.filteredinvites = this.invitedUsers.filter(({ Accepted }) => Accepted == true)
		// }
		// if (this.choiceSelected == 2) {// "Canceled") {
		// 	this.filteredinvites = this.invitedUsers.filter(({ Canceled }) => Canceled == true);
		// 	this.showingCancelled = true;
		// }

	}


	/**
	 * Filter the Invites List
	 * @param showAll
	 */
	tabChange(tabEvent: any) {
		this.showingCancelled = false;
		if (!this.selectedAccount.Admin) {
			this.choiceSelected = 0; //"Pending"
		}
		this.setChoice(tabEvent.selectingIndex)

	}

	setChoice(choice: number) {
		if (choice == 0) {
			this.filteredinvites = this.invitedUsers.filter(({ Canceled, Accepted }) => Canceled == false && Accepted == false)
		} else if (choice == 1) {
			this.filteredinvites = this.invitedUsers.filter(({ Accepted }) => Accepted == true)
		} else if (choice == 2) {
			this.filteredinvites = this.invitedUsers.filter(({ Canceled }) => Canceled == true);
			this.showingCancelled = true;
		} else { this.filteredinvites = [] }
	}

	/**
	 * Get List of Account Users
	 * @param accountGUID
	 */
	async getAccountUsers(accountGUID: string) {

		try {
			const response = await firstValueFrom(
				this.accountsService.getAccountUsers$(this.accountGUID, { "systemAccount": accountGUID, "showDisabled": false, "showDeleted": true })
			);
			this.siteSubscriptions.setAccountUsers(<IAccountUser[]>response);
		} catch (error: any) {
			if (error.status != 404) {
				console.error(error)
				this.globalFunctions.HandleError(error, "this.accountsService.getAccountUsers$: Error retrieving Users")
			} else {
				// console.error(error.error);
				this.siteSubscriptions.setAccountUsers(<IAccountUser[]>[]);
			}
		}
		finally { }


		// // Create a local Subject to control the subscription lifecycle
		// const fetchDataSubject = new Subject<void>();

		// this.accountUsers = []
		// // Get Users for the account
		// this.accountsService.getAccountUsers$(this.accountGUID, { "systemAccount": accountGUID, "showDisabled": false }).pipe(
		// 	takeUntil(fetchDataSubject), distinctUntilChanged(),
		// 	filter((users) => !!users),
		// 	finalize(() => {
		// 		// Ensure the subscription is terminated and the subject is completed
		// 		fetchDataSubject.next();
		// 		fetchDataSubject.complete();
		// 	})
		// ).subscribe({
		// 	next: (users) => {
		// 		try {
		// 			this.siteSubscriptions.setAccountUsers(<IAccountUser[]>users)
		// 		} catch (error) { this.globalFunctions.HandleError(error, "") }
		// 	},
		// 	error: (error: HttpErrorResponse) => {

		// 		if (error.status != 404) {
		// 			console.error(error)
		// 			this.globalFunctions.HandleError(error, "this.accountsService.getAccountUsers$: Error retrieving Invitations")
		// 		} else {
		// 			console.error(error.error);
		// 		}
		// 	},
		// 	//complete: () => { console.log("this.accountsService.getAccountUsers$: Complete"); }
		// });
	}

	/**
	 * View the Image Fullsize
	 * @param imageURL
	 */
	viewImage(imageURL: string) {
		this.globalFunctions.viewImageFullSize(imageURL);

	}

	/**
	 * Retrieve an Account User
	 * @param UserID
	 * @returns
	 */
	deleteAccountUser(userID: number) {
		if (!this.selectedAccount.Admin && userID != this.thisUser.UserID) { return }
		let user = this.accountUsers.find(({ UserID }) => UserID == userID);

		if (user) {
			if (userID == this.selectedAccount.OwnerID) {
				this.globalFunctions.UserConfirm$("You cannot delete the Account Owner")
				return;
			}
			if (user.Admin) { return }

			if (userID == this.thisUser.UserID) {
				this.globalFunctions.UserConfirm$("Are you sure you wish to leave the account?").subscribe(OKResult => {
					if (OKResult) {
						this.removeUser(user);
					}
				});
			}
			else {
				this.globalFunctions.UserConfirm$("Remove User " + user.Name + "?").subscribe(OKResult => {
					if (OKResult) {
						this.removeUser(user);
					}
				});
			}
		}
	}

	/**
	 * removeUser
	 * @param user user 
	 */
	removeUser(user: IAccountUser) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		user.Deleted = true
		this.accountsService.deleteAccountUser$(this.accountGUID, user.UserID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((response) => !!response),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (response) => {
				this.getAccountUsers(this.accountGUID);
			},
			error: (error) => {
				console.error(error)
				this.globalFunctions.HandleError(error, "setAdmin: Error changing Admin Status")
			},
			complete: () => { }
		});
	}

	/**
	 *
	 * @param event
	 */
	toggleChange(event: any) {
		// console.log(event);
	}

	/**
	 * change User Group Memberships
	 * @param user
	 */
	changeGroups(user: IAccountUser) {
		alert("Function not implemented yet");
	}

	/**
	 * Change User Admin Status
	 * @param admin
	 * @param UserID
	 * @returns
	 */
	async changeAdmin(admin: boolean, ID: number) {

		let confirmMessage: string
		let user = this.accountUsers.find(({ UserID }) => UserID == ID)

		/** You need to be an Account admin or Account Owner */
		if ((!this.selectedAccount.Admin || !ID) && user?.UserID != this.selectedAccount.OwnerID) { return; }

		/** Cannot Demote the Account Owner */
		if ((user?.UserID == this.selectedAccount.OwnerID) && admin == false) {
			this.globalFunctions.UserConfirm$("You cannot delete the Account Owner")
			return;
		}

		/** Cannot Demote yourself????? */
		if ((user?.UserID == this.thisUser.UserID) && admin == false) {
			const OKResult = await firstValueFrom(
				this.globalFunctions.UserConfirm$("Do you wish to demote yourself from Administrator")
			);
			if (!OKResult) { return; }
		}

		if (user) {
			confirmMessage = "";
			if (admin) { confirmMessage = "Promote " + user.Name + " to Account Admin?" };
			if (!admin) { confirmMessage = "Demote " + user.Name + " to Account User?" };
			this.globalFunctions.UserConfirm$(confirmMessage).subscribe(async OKResult => {
				if (OKResult) {
					user.Admin = admin;
					this.patchUser(user);
				}
			});
		}
	}

	/**
	 * Change User Email Status
	 * @param send
	 * @param UserID
	 * @returns
	 */
	changeSendEmail(send: boolean, ID: number) {

		let confirmMessage: string
		let user = this.accountUsers.find(({ UserID }) => UserID == ID)

		/** You need to be an Account admin or Account Owner */
		if ((!this.selectedAccount.Admin || !ID) && user?.UserID != this.selectedAccount.OwnerID) { return; }

		if (user) {
			confirmMessage = "";
			if (send) { confirmMessage = "Send " + user.Name + " Emails?" };
			if (!send) { confirmMessage = "Do Not Send " + user.Name + " Emails?" };
			this.globalFunctions.UserConfirm$(confirmMessage).subscribe(async OKResult => {
				if (OKResult) {
					user.Emails = send;
					this.patchUser(user);
				}
			});
		}
	}

	/**
	 * changeUserEnabled
	 * @param enabled enabled
	 * @param ID ID
	 */
	changeUserEnabled(enabled: boolean, ID: number) {
		let confirmMessage: string
		let user = this.accountUsers.find(({ UserID }) => UserID == ID)

		/** If we don't have an ID, the user isn't an admin or they are trying to disable the owner */
		if (!ID || (!this.selectedAccount.Admin && !this.thisUser.AdminSite) || user?.UserID == this.selectedAccount.OwnerID) {
			return;
		}

		if (user) {
			confirmMessage = "";
			if (enabled) { confirmMessage = "Enable " + user.Name + "?" };
			if (!enabled) { confirmMessage = "Disable " + user.Name + "?" };
			this.globalFunctions.UserConfirm$(confirmMessage).subscribe(async OKResult => {
				if (OKResult) {
					user.AccountUserDisabled = !enabled;
					this.patchUser(user);
				}
			});
		}
	}

	/**
	 * patchUser
	 * @param user
	 */
	patchUser(user: IAccountUser, recovery?: boolean) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.accountsService.patchAccountUser$(this.accountGUID, user.UserID, user).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((response) => !!response),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (response) => {
				// if (response && !this.accountUsers) {
				this.getAccountUsers(this.accountGUID);
				this.refreshAccountsList();
				if (recovery) {
					this.globalFunctions.DisplaySnackBar({
						message: "User Recovered",
						duration: 2000,
						action: ""
					});
				}
			},
			error: (error) => {
				console.error(error)
				this.globalFunctions.HandleError(error, "setAdmin: Error changing Admin Status")
			},
			complete: () => { }
		});
	}

	/** Refresh My Accounts Subscription */
	refreshAccountsList() {
		let data = {
			"userPublicID": this.thisUser.Sub,
			"showDeleted": this.thisUser.AdminSite
		}
		this.siteSubscriptions.RefreshMyAccounts(data)
	}

	/**
	 * Cancel an invitation
	 * @param inviteID
	 * @returns
	 */
	cancelInvite(inviteID: any) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		/** You need to ba an admin */
		if (!this.selectedAccount.Admin) { return }

		// Find the Invite
		this.selectedInvite = <IAccountInviteDetail>this.invitedUsers.find(({ InviteGUID }) => InviteGUID == inviteID)

		if (this.selectedInvite) {

			const dialogRef = this.dialog.open(CommentDialogComponent, {
				width: '500px',
				data: {
					"HeaderText": "Cancel Invitation",
					"DialogText": "Cancel the Invitation for " + this.selectedInvite.InviteName + "?"
				}
			});
			dialogRef.afterClosed().pipe(
				filter(result => !!result)
			).subscribe(result => {
				// Perform the delete action with the comment
				// console.log('The dialog was closed with comment:', result.comment);
				// Add your delete logic here
				this.selectedInvite.CancelReason = result.comment;

				this.globalFunctions.displaySpinner(true)
				this.inviteService.inviteCancel$(this.accountGUID, this.selectedInvite).pipe(
					takeUntil(fetchDataSubject), distinctUntilChanged(),
					filter((response) => !!response),
					finalize(() => {
						// Ensure the subscription is terminated and the subject is completed
						fetchDataSubject.next();
						fetchDataSubject.complete();
					})
				).subscribe({
					next: (response) => {
						// console.log(response)
						this.getAccountInvites(this.accountGUID)

						// this.globalFunctions.displaySpinner(false)
						this.globalFunctions.DisplaySnackBar({
							message: "Invitation to " + this.selectedInvite?.InviteName + " has been canceled",
							duration: 2000,
							action: ""
						})
					},
					error: (error) => {
						this.globalFunctions.displaySpinner(false)
						this.globalFunctions.HandleError(error, "cancelInvite: Error cancelling Invite")
					},
					complete: () => { this.globalFunctions.displaySpinner(false) }
				});
			});
		}
	}

	/**
	 * Patch the selected Account
	 * @param account
	 */
	sendInvite(invite: IAccountInviteDetail = <IAccountInviteDetail>{}, resend: boolean = false) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		/** You need to ba an admin */
		if (!this.selectedAccount.Admin) { return }

		// Create a new Account Invitation
		var _edit = <IAccountInvite>{
			"InviteName": invite.InviteName || "",
			"InviteEmail": invite.InviteEmail || "",
			"InviteMessage": invite.InviteMessage || "",
			"InviteAsAdmin": invite.InviteAsAdmin || false,
			"Resend": resend
		}

		// Display the dialog box
		const dialogRef = this.dialog.open(UserInvitationComponent, {
			data: _edit,
		});

		// Update the original Asset with the result
		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
			// console.log('The dialog was closed');
			// Check we have a result to work with
			this.globalFunctions.displaySpinner(true)
			Object.assign(_edit, result);

			let serverAddress = window.location.protocol + "//" + window.location.host
			// serverAddress = 'https://prismpdm.io/'

			//Update the Account GID
			_edit.AccountUID = this.selectedAccount.GUID
			_edit.InviteUserID = this.thisUser.Sub
			_edit.InviteServerURL = serverAddress

			this.inviteService.inviteAccountUser$(this.accountGUID, _edit).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((response) => !!response),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (response) => {
					if (response['Error'] != "") {

					} else {
						// this.globalFunctions.displaySpinner(false)
						this.globalFunctions.DisplaySnackBar({
							message: response['Result'],
							duration: 2000,
							action: "",
							type: SnackType.Info
						})
						this.getAccountInvites(this.accountGUID)
						this.choiceSelected = "Pending"
					}

				},
				error: (error) => {
					this.globalFunctions.displaySpinner(false)
					if (error.status = 404) {
						this.globalFunctions.DisplaySnackBar({
							message: error.error,
							duration: 2000,
							action: "",
							type: SnackType.Error
						})
						return
					}
					this.globalFunctions.HandleError(error, "sendInvite: Error Sending Invite")
				},
				complete: () => { this.globalFunctions.displaySpinner(false) }
			});
		});
	}

	/**
	 * Resend a User Invite
	 * @param inviteID
	 * @returns
	 */
	resendInvite(inviteID: any) {
		/** You need to ba abn admin */
		if (!this.selectedAccount.Admin) { return }
		// Find the Invite
		let invite = this.invitedUsers.find(({ InviteGUID }) => InviteGUID.toUpperCase() === inviteID.toUpperCase())
		if (invite) { this.sendInvite(invite, true) }
	}

	/**
	 * convertEpoch
	 * @param epoch
	 * @returns
	 */
	convertEpoch(epoch: number): string {
		/** Use the Global Function */
		return convertEpochToDateString(epoch);
	}

	addUser() {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		// Create a 'Local'/shallow copy of the Asset Information
		var _edit = {
			currentUserIDs: this.accountUsers.map(user => user.UserID),
			userID: 0,
			admin: false
		};

		// Display the dialog box
		const dialogRef = this.dialog.open(AddAccountUserComponent, {
			data: _edit,
		});

		// Update the original Asset with the result
		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {

			var userID = result.userID
			var admin = result.admin

			// console.log('The dialog was closed');
			// Check we have a result to work with
			this.accountsService.postAccountUsers$(this.accountGUID, userID, admin).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((SQLResult) => !!SQLResult),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (SQLResult) => {
					let response = <ISQLResult>SQLResult
					if (response.Error == "") {
						this.getAccountUsers(this.accountGUID);
					}
				}, error: (error) => {
					console.error(error);
				}
			})
		});
	}

	/**
	 * refreshAccountUsers
	 */
	refreshAccountUsers() {
		// Get Users for the account
		this.getAccountUsers(this.accountGUID);

		// Get Users for the account
		this.getAccountInvites(this.accountGUID);
	}

	/**
	 * showWebsiteAdmins
	 * @param event event
	 */
	showWebsiteAdmins(event: any) {
		let show: boolean = typeof (event) == 'boolean' ? event : event.checked;
		this.showingWebsiteAdmins = show;
		if (!show) {
			this.filteredAccountUsers = new MatTableDataSource(this.accountUsers.filter((x: IAccountUser) => !x.AdminSite));
		} else {
			this.filteredAccountUsers = new MatTableDataSource(this.accountUsers);
		}
	}

	/**
	 * Key up event from the search input on HTML
	 * @param event HTMLInputElement
	 */
	applyFilter(value: string) {
		try {
			const filterValue = value.toLowerCase();

			this.filteredAccountUsers.filterPredicate = (data: IAccountUser, filter: string) => {
				/** Build a string per row to filter and convert to lower case */
				const textToSearch = (data.Name + data.Email).toLowerCase() || '';
				return textToSearch.indexOf(filter) !== -1;
			};

			this.filteredAccountUsers.filter = filterValue.trim().toLowerCase();

			// Check if there are no matches and display a message
			if (this.filteredAccountUsers.filteredData.length == 0) {
				this.globalFunctions.DisplaySnackBar({
					message: "No matches for '" + filterValue.trim().toLowerCase() + "'",
					type: SnackType.Alert,
					duration: 1000
				})
			}
		}
		catch (error) { this.globalFunctions.HandleError(error, "Error filtering list") }
	}

	/**
	 * Get Account Roles Label
	 * @param role
	 * @returns
	 */
	getSafeAccountRoleLabel(role: AccountRole): string {
		return AccountRoleLabels[role] ?? 'Unknown Role';
	}

	/**
	 * editAccountUserRole
	 * @param user user 
	 */
	editAccountUserRole(user?: IAccountUser) {
		this.editingUserRole = user ? user : undefined;
	}

	/**
	 * patchUserRole
	 * @param event event 
	 * @param user user 
	 */
	patchUserRole(event: any, user: IAccountUser) {
		user.AccountRole = event.value;
		this.patchUser(user);
	}

	/**
	 * recoverUser
	 * @param user user  
	 */
	recoverUser(user: IAccountUser) {
		this.globalFunctions.UserConfirm$("Recover User " + user.Name + "?").subscribe(OKResult => {
			if (OKResult) {
				user.Deleted = false;
				this.patchUser(user, true);
			}
		});
	}
}
